import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, catchError, map, of, shareReplay } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AmplitudeService } from '../analytics/amplitude.service';
import { UserProfile } from './auth.models';

@Injectable({
  providedIn: 'root',
})
export class AuthProfileService {
  private readonly http = inject(HttpClient);
  private readonly amplitudeService = inject(AmplitudeService);

  private readonly apiUrl = environment.apiUrl;

  private readonly userProfile$$ = new BehaviorSubject<UserProfile | null>(null);

  private profileRequest$: Observable<UserProfile> | null = null;

  getProfile(): Observable<UserProfile> {

    const cached = this.userProfile$$.value;
    if (cached) {
      return of(cached);
    }

    if (this.profileRequest$) {
      return this.profileRequest$;
    }

    this.profileRequest$ = this.http.get<UserProfile>(`${this.apiUrl}/auth/profile`).pipe(
      tap((profile) => {
        this.userProfile$$.next(profile);
        this.profileRequest$ = null;
        this.updateAmplitudeUserProperties(profile);
      }),
      catchError((error) => {
        this.profileRequest$ = null;
        throw error;
      }),
      shareReplay(1),
    );

    return this.profileRequest$;
  }

  /**
   * Force refresh user profile from API.
   * Use after subscription changes or other profile updates.
   */
  refreshProfile(): Observable<UserProfile> {
    this.profileRequest$ = null;
    return this.http.get<UserProfile>(`${this.apiUrl}/auth/profile`).pipe(
      tap((profile) => {
        this.userProfile$$.next(profile);
        this.updateAmplitudeUserProperties(profile);
      }),
      shareReplay(1),
    );
  }

  /** Clear cached profile (e.g. on logout). */
  clearProfile(): void {
    this.userProfile$$.next(null);
    this.profileRequest$ = null;
  }

  // ============================================
  // ROLE CHECKS
  // ============================================

  /**
   * Check if a given user profile represents a student.
   * Use this when you already have a profile object in hand.
   */
  hasStudentRole(profile: UserProfile): boolean {
    return profile.roles?.includes('student') ?? false;
  }

  /**
   * Check if a given user profile represents a teacher.
   * Teachers are users without the 'student' role (teacher by default).
   * Use this when you already have a profile object in hand.
   */
  hasTeacherRole(profile: UserProfile): boolean {
    return !this.hasStudentRole(profile);
  }

  /**
   * Check if the current user is a student.
   * @param forceRefresh If true, fetches a fresh profile from the API instead of using cache.
   */
  isStudent(forceRefresh = false): Observable<boolean> {
    const profile$ = forceRefresh ? this.refreshProfile() : this.getProfile();
    return profile$.pipe(map((profile) => this.hasStudentRole(profile)));
  }

  /**
   * Check if the current user is a teacher (not a student).
   * @param forceRefresh If true, fetches a fresh profile from the API instead of using cache.
   */
  isTeacher(forceRefresh = false): Observable<boolean> {
    const profile$ = forceRefresh ? this.refreshProfile() : this.getProfile();
    return profile$.pipe(map((profile) => this.hasTeacherRole(profile)));
  }

  // ============================================
  // SUBSCRIPTION CHECKS
  // ============================================

  /**
   * Check if user has premium subscription (active or trialing).
   * Returns false if profile hasn't been loaded yet.
   */
  hasPremium(): boolean {
    const profile = this.userProfile$$.value;
    const status = profile?.subscription?.status;
    return status === 'active' || status === 'trialing';
  }

  // ============================================
  // PRIVATE
  // ============================================

  private updateAmplitudeUserProperties(profile: UserProfile): void {
    this.amplitudeService.setUserId(profile.id);
    this.amplitudeService.setUserProperties({
      email: profile.email,
      providerType: profile.providerType,
      subscriptionStatus: profile.subscription?.status ?? 'none',
      isTrial: profile.subscription?.isTrial ?? false,
      plan: profile.subscription?.plan ?? null,
    });
  }
}
